import {
  Firestore,
  WriteBatch,
  doc
} from 'firebase/firestore';
import { StorageResolver } from '@/firebase/firestore/storage-resolver';
import { nestObject } from '@/lib/utils/firestore-nest';
import type { Novel } from '@/lib/types';

export function addEntitySaveToBatch<T extends { id: string }>(
  batch: WriteBatch,
  resolver: StorageResolver,
  novel: Novel,
  family: 'entities' | 'narrative',
  type: string,
  item: T,
  isNew: boolean = false
): void {
  const layout = novel.storageLayout;
  const familyLayout = family === 'entities' ? layout.entities : layout.narrative;
  const mode = familyLayout[type]?.items || 'embedded';
  const resolved = resolver.resolveItem(novel.ownerId, novel.id, family, type, item.id, layout);
  const ref = resolved.ref;

  const cleanData: Record<string, unknown> = { ...item };
  Object.keys(cleanData).forEach(k => {
    if (cleanData[k] === undefined) delete cleanData[k];
  });

  if (mode === 'embedded') {
    const { id, ...rest } = cleanData;
    const updates: Record<string, unknown> = {};
    if (isNew) {
      updates[`entityList.${id}`] = cleanData;
    } else {
      Object.entries(rest).forEach(([key, value]) => {
        updates[`entityList.${id}.${key}`] = value;
      });
    }
    batch.set(ref, nestObject(updates), { merge: true });
  } else {
    if (isNew) {
      batch.set(ref, cleanData);
    } else {
      batch.set(ref, cleanData, { merge: true });
    }
  }
}

export function addSummaryPathUpdateToBatch(
  firestore: Firestore,
  batch: WriteBatch,
  novel: Novel,
  family: 'entities' | 'narrative',
  type: string,
  itemId: string,
  pathAfterItemId: string,
  value: unknown
): void {
  const layout = novel.storageLayout;
  const familyLayout = family === 'entities' ? layout.entities : layout.narrative;
  const metaMode = familyLayout[type]?.meta || 'embedded';
  const metaFamily = family === 'entities' ? 'entitiesMeta' : 'narrativeMeta';

  if (metaMode === 'split') {
    const metaRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, metaFamily, type);
    batch.set(metaRef, nestObject({
      [`summaries.${itemId}.${pathAfterItemId}`]: value
    }), { merge: true });
  } else {
    const novelRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id);
    batch.update(novelRef, {
      [`${metaFamily}.summaries.${type}.${itemId}.${pathAfterItemId}`]: value
    });
  }
}
